import { SearchInput, Input } from "@design-system/shadcn";
import React, { useMemo, useCallback, ChangeEvent } from "react";

import { useFieldContext } from "../core/form-contexts";
import { useFieldDescribedBy } from "../core/use-field-described-by";

type FormFieldInputProps = Omit<
  React.ComponentProps<typeof Input>,
  "name" | "hasError" | "value" | "onChange" | "onBlur"
> & { debounceMs?: number };

function FormFieldInput({ debounceMs, ...props }: FormFieldInputProps) {
  const field = useFieldContext<string>();

  if (!field) {
    throw new Error("FormFieldInput must be used within form.AppField");
  }

  const hasError = useMemo(
    () => field.state.meta.errors.length > 0,
    [field.state.meta.errors.length]
  );

  const describedBy = useFieldDescribedBy(props["aria-describedby"]);
  const changeHandler = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value),
    [field]
  );

  const labelId = `${field.name}-label`;

  if (debounceMs && debounceMs > 0) {
    return (
      <SearchInput
        id={field.name}
        name={field.name}
        aria-invalid={hasError}
        aria-labelledby={labelId}
        value={field.state.value}
        onChange={changeHandler}
        onBlur={field.handleBlur}
        debounceMs={debounceMs}
        {...props}
        aria-describedby={describedBy}
      />
    );
  }

  return (
    <Input
      id={field.name}
      name={field.name}
      aria-invalid={hasError}
      aria-labelledby={labelId}
      value={field.state.value}
      onChange={changeHandler}
      onBlur={field.handleBlur}
      {...props}
      aria-describedby={describedBy}
    />
  );
}

export { FormFieldInput };
export type { FormFieldInputProps };
